import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Modal from "../../../common/components/ThemedModal";
import { BrandTeam } from "../../../../types/model";

interface EditBrandTeamModalProps {
	show: boolean;
	onHide: () => void;
	brandTeam: BrandTeam;
	handleUpload: (file: File) => void;
	handleInputChange: (field: keyof BrandTeam, value: any) => void;
	onSubmit: () => void;
}

const EditBrandTeamModal: React.FC<EditBrandTeamModalProps> = ({
	show,
	onHide,
	brandTeam,
	handleUpload,
	handleInputChange,
	onSubmit,
}) => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFilePreview, setSelectedFilePreview] = useState<string>("");

	useEffect(() => {
		if (!selectedFile) {
			setSelectedFilePreview("");
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		setSelectedFilePreview(objectUrl);

		return () => {
			URL.revokeObjectURL(objectUrl);
		};
	}, [selectedFile]);

	useEffect(() => {
		if (!show) {
			setSelectedFile(null);
			setSelectedFilePreview("");
		}
	}, [show]);

	const handleSubmit = () => {
		onSubmit();
		onHide();
	};

	const handleUploadImage = async () => {
		if (!selectedFile) {
			alert("Please select an image file to upload");
			return;
		}

		handleUpload(selectedFile);
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>Edit Brand Team</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form>
					<Form.Group className="mb-3" controlId="teamName">
						<Form.Label>Team Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter team name"
							value={brandTeam.name}
							onChange={(e) => handleInputChange("name", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamShortName">
						<Form.Label>Short Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter short name"
							value={brandTeam.shortName}
							onChange={(e) => handleInputChange("shortName", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamCountry">
						<Form.Label>Country</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter country"
							value={brandTeam.country}
							onChange={(e) => handleInputChange("country", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamSponsor">
						<Form.Label>Sponsor</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter sponsor"
							value={brandTeam.sponsor}
							onChange={(e) => handleInputChange("sponsor", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamColor">
						<Form.Label>Color</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter color"
							value={brandTeam.color}
							onChange={(e) => handleInputChange("color", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamLogoUrl">
						<Form.Label>Logo URL</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter logo URL"
							value={brandTeam.logoUrl}
							onChange={(e) => handleInputChange("logoUrl", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamSource">
						<Form.Label>Source</Form.Label>
						<Form.Control
							type="text"
							placeholder="Enter source URL"
							value={brandTeam.source}
							onChange={(e) => handleInputChange("source", e.target.value)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamIsActive">
						<Form.Check
							type="checkbox"
							label="Is Active"
							checked={brandTeam.isActive}
							onChange={(e) => handleInputChange("isActive", e.target.checked)}
						/>
					</Form.Group>
					<Form.Group className="mb-3" controlId="teamFeatured">
						<Form.Check
							type="checkbox"
							label="Featured"
							checked={brandTeam.featured}
							onChange={(e) => handleInputChange("featured", e.target.checked)}
						/>
					</Form.Group>
					{brandTeam.imageUrl && (
						<Form.Group className="mb-3" controlId="teamImagePreview">
							<Form.Label>Current Image</Form.Label>
							<div>
								<img
									src={brandTeam.imageUrl}
									alt="Brand Team"
									style={{ maxWidth: "100%", maxHeight: "200px" }}
								/>
							</div>
						</Form.Group>
					)}
					{selectedFilePreview && (
						<Form.Group className="mb-3" controlId="selectedImagePreview">
							<Form.Label>Selected Image Preview</Form.Label>
							<div>
								<img
									src={selectedFilePreview}
									alt="Selected Brand Team Preview"
									style={{ maxWidth: "100%", maxHeight: "220px" }}
								/>
							</div>
						</Form.Group>
					)}
					<Form.Group
						className="mb-3 d-flex align-items-center"
						controlId="teamImageUrl"
					>
						<Form.Control
							type="file"
							accept="image/png, image/jpeg, image/jpg"
							onChange={(e: any) => {
								if (e.target.files && e.target.files[0]) {
									const file = e.target.files[0];
									setSelectedFile(file);
								}
							}}
							className="me-2"
						/>
						<Button
							type="button"
							variant="outline-primary"
							onClick={handleUploadImage}
						>
							Upload
						</Button>
					</Form.Group>
				</Form>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onHide}>
					Cancel
				</Button>
				<Button variant="primary" onClick={handleSubmit}>
					Save Changes
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default EditBrandTeamModal;
